import { requestJson } from './http';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

function headers(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface HouseholdPreferences {
  timezone: string;
  timezone_source: string;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  render_ready_email: boolean;
}

export interface HouseholdMember {
  membership_id: string;
  user_id: string;
  display_name: string;
  email: string;
  role: 'owner' | 'admin' | 'adult';
  state: string;
  profile_picture_url: string | null;
  is_current_user: boolean;
}

export interface HouseholdListener {
  listener_id: string;
  display_name: string;
  age_band: string | null;
  state: 'active' | 'archived';
}

export interface Household {
  household_id: string;
  state: string;
  current_user_role: 'owner' | 'admin' | 'adult';
  preferences: HouseholdPreferences;
  members: HouseholdMember[];
  listeners: HouseholdListener[];
}

export async function fetchHouseholds(signal?: AbortSignal): Promise<Household[]> {
  return requestJson<Household[]>(`${API_BASE_URL}/households`, { headers: headers(), signal });
}

export async function updateHouseholdPreferences(
  householdId: string,
  preferences: Partial<Pick<HouseholdPreferences,
    'timezone' | 'quiet_hours_start' | 'quiet_hours_end' | 'render_ready_email'>>
): Promise<HouseholdPreferences> {
  return requestJson<HouseholdPreferences>(
    `${API_BASE_URL}/households/${encodeURIComponent(householdId)}/preferences`,
    { method: 'PATCH', headers: headers(), body: JSON.stringify(preferences) }
  );
}

export async function createHouseholdListener(
  householdId: string,
  displayName: string,
  ageBand: string | null
): Promise<HouseholdListener> {
  return requestJson<HouseholdListener>(
    `${API_BASE_URL}/households/${encodeURIComponent(householdId)}/listeners`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ display_name: displayName, age_band: ageBand }),
    }
  );
}

export async function removeHouseholdMember(
  householdId: string,
  membershipId: string
): Promise<{ membership_id: string; state: string; cache_epoch: number }> {
  return requestJson<{ membership_id: string; state: string; cache_epoch: number }>(
    `${API_BASE_URL}/households/${encodeURIComponent(householdId)}/members/${encodeURIComponent(membershipId)}`,
    { method: 'DELETE', headers: headers() }
  );
}
